import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import styled from 'styled-components';

import { Link } from 'core/routing';

import { Div } from 'legacyComponents/styled/lib';
import { ButtonGroup } from 'legacyComponents/styles';
import { withConditional } from 'legacyComponents/hoc/conditional';

import { Process } from 'apps/processMonitor/views/process';
import {
  Main,
  MainTitle,
  ContribSectionTitle,
  ProcessWrapper,
  ProcessName,
  IconStyled,
} from 'apps/processMonitor/styles';

import { Dot } from 'apps/components/shared';
import { Button } from 'components';

import { withNamespaces, Trans } from 'react-i18next';

const MaximizedSection = withConditional(styled.div``);
const ConditionalSection = withConditional(styled.div``);
const ProjectLink = withConditional(Link);
const ContributeButton = withConditional(Button);
const ConditionalText = withConditional(React.Fragment);

const ActionButtonGroup = styled(ButtonGroup)`
  padding-top: 5px;
  padding-bottom: 10px;
`;

const sharedFileRoutes = [
  'share-file',
  'profile.shared.file',
  'initiative.shared.file',
];

@withNamespaces()
@inject('clientStore', 'processMonitor', 'routerStore')
@observer
export class ProcessMonitor extends React.Component {
  toggleMinimize = () => {
    const { processMonitor } = this.props;
    const { minimised } = processMonitor;
    processMonitor.SetMinimised(!minimised);
  };

  contribute = () => {
    const { clientStore, processMonitor } = this.props;
    processMonitor.SetMinimised(true);
    const repoAppManager = clientStore.context.get('repoAppManager');
    repoAppManager.ContributionForm();
  };

  render() {
    const { clientStore, processMonitor, routerStore } = this.props;
    const { currentProcess, minimised, SetMinimised } = processMonitor;
    if (!clientStore.uploader) return null;

    const { queue, uploadsQueue } = clientStore.uploader;
    const isSharedFileRoute = sharedFileRoutes.includes(
      routerStore.route?.name,
    );
    const isViewerRoute = routerStore.route?.name === 'viewer';

    if (
      isSharedFileRoute ||
      isViewerRoute ||
      !currentProcess ||
      processMonitor.loading
    ) {
      return null;
    }

    const { pendingOperationProjects } = currentProcess;

    const pendingCount = pendingOperationProjects.length;
    const uploadCount = queue.length + uploadsQueue.length;

    if (pendingCount + uploadCount === 0) {
      return null;
    }

    const repoManager = clientStore.context.get('repoAppManager');
    if (clientStore.routerStore.route.name.includes('project.conflicts')) {
      return null;
    }

    return (
      <Main minimised={minimised}>
        <Div position='relative'>
          <MainTitle onClick={this.toggleMinimize} minimised={minimised}>
            <Dot>●</Dot>
            <ConditionalText condition={uploadCount === 0 && pendingCount > 0}>
              <Trans count={pendingCount}>
                {{ pendingCount }} Contribution pending
              </Trans>
            </ConditionalText>
            <ConditionalText condition={uploadCount > 0}>
              <Trans count={uploadCount}>
                Uploading {{ uploadCount }} item...
              </Trans>
            </ConditionalText>
          </MainTitle>
          <IconStyled
            onClick={this.toggleMinimize}
            name={minimised ? 'caret_up' : 'caret_down'}
          />
        </Div>
        <MaximizedSection condition={!minimised}>
          {pendingOperationProjects.map(project => {
            if (!project) {
              return null;
            }
            const { id, name, spacePath, slug } = project;
            const spaceType = spacePath[0] === '+' ? 'initiative' : 'profile';
            const route = `${spaceType}.project`;
            const isAtRoute = clientStore.routerStore.route.name.includes(
              'project.files',
            );
            const isAtProject =
              !!repoManager &&
              repoManager.parent.project &&
              repoManager.parent.project.id === project.id &&
              isAtRoute;
            const linkParams = { [spaceType]: spacePath, slug };
            return (
              <div key={id}>
                <ContribSectionTitle>{name}</ContribSectionTitle>
                <ProcessWrapper>
                  <ProcessName>
                    <Trans>All files synced to cloud successfully.</Trans>
                  </ProcessName>
                </ProcessWrapper>
                <ActionButtonGroup>
                  <ProjectLink
                    condition={!isAtProject}
                    name={`${route}.files`}
                    params={linkParams}
                  >
                    <Button
                      size='small'
                      onClick={() => SetMinimised(true)}
                      text={<Trans>View local changes</Trans>}
                    />
                  </ProjectLink>
                  <ContributeButton
                    condition={isAtProject}
                    onClick={this.contribute}
                    size='small'
                    text={<Trans>Contribute</Trans>}
                    disabled={!repoManager?.repository?.contributionReady}
                  />
                </ActionButtonGroup>
              </div>
            );
          })}
          <ConditionalSection condition={uploadsQueue.length > 0}>
            <ContribSectionTitle>
              <Trans>Uploading</Trans>
            </ContribSectionTitle>
            {uploadsQueue.map((fileManager, i) => (
              <Process key={i} fileManager={fileManager} />
            ))}
          </ConditionalSection>
          <ConditionalSection condition={queue.length > 0}>
            <ContribSectionTitle>
              <Trans>Queue</Trans>
            </ContribSectionTitle>
            {queue.map((fileManager, i) => (
              <Process key={i} fileManager={fileManager} />
            ))}
          </ConditionalSection>
        </MaximizedSection>
      </Main>
    );
  }
}

ProcessMonitor.propTypes = {
  clientStore: PropTypes.object,
  processMonitor: PropTypes.object,
  routerStore: PropTypes.object,
};
